/* eslint-disable import/no-cycle */

import React, { useMemo } from 'react';

import { Icon } from '@meterup/atto';
import type { AxiosAPIError, QuoteRequest } from '@meterup/connect-api';
import { axios } from '@meterup/connect-api';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import BaseLayout from '../../components/Layout/BaseLayout';
import SidebarLayout from '../../components/Layout/SidebarLayout';
import ConfirmQuoteBlock from '../../components/Quotes/ConfirmQuoteBlock/ConfirmQuoteBlock';
import QuotesContractSidebar from '../../components/QuotesContract/QuotesContractSidebar/QuotesContractSidebar';
import useQuotes from '../../hooks/useQuotes';

function LocationHeader({ quoteRequest }: { quoteRequest: QuoteRequest }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 ml-1 -mt-0.5">
        <Icon icon="location" size={20} />
      </span>
      <span className="font-semibold text-gray-800 whitespace-nowrap">
        {quoteRequest?.location?.address?.address1},
      </span>
      <span className="text-gray-400 whitespace-nowrap">
        {quoteRequest?.location?.address?.city}, {quoteRequest?.location?.address?.state}
      </span>
    </div>
  );
}

function QuoteRequestsCart() {
  const { sid } = useParams<{ sid: string }>();
  const { data: quoteRequest, isLoading: loading } = useQuery<unknown, AxiosAPIError, QuoteRequest>(
    ['quoteRequest', sid],
    () => axios.get(`v1/connect/quote-requests/${sid}`),
    {
      enabled: !!sid,
    },
  );

  const selectedQuoteSids = useMemo(
    () => (quoteRequest ? quoteRequest.selectedQuotes : []),
    [quoteRequest],
  );

  const { loadingQuotes, selectedQuotes } = useQuotes(sid!, selectedQuoteSids);

  const renderSidebar = () => {
    if (!selectedQuotes.length) {
      return null;
    }
    if (!quoteRequest) {
      return null;
    }
    return (
      <div className="pb-8 md:pb-0">
        <QuotesContractSidebar selectedQuotes={selectedQuotes} quoteRequest={quoteRequest} />
      </div>
    );
  };

  if (loading || loadingQuotes) {
    return null;
  }

  if (selectedQuotes.length < 1) {
    return (
      <BaseLayout align="top">
        <div className="mx-auto max-w-2xl max-w-screen-xs w-full border border-gray-100 p-10 mt-10">
          {quoteRequest && <LocationHeader quoteRequest={quoteRequest} />}
          <div className="flex flex-col ml-1 mt-1">
            Please check back soon for recommended providers.
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <SidebarLayout sidebarChildren={renderSidebar()} hideSidebarOnMobile={false} align="top">
      <div className="mx-auto max-w-2xl max-w-screen-xs w-full">
        {quoteRequest && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
            <LocationHeader quoteRequest={quoteRequest} />
            <div className="text text-gray-700 ml-1">Recommended connections</div>
          </div>
        )}
        {selectedQuotes.map((q) => (
          <div key={q.sid} className="mt-4 rounded-md overflow-hidden border border-gray-200">
            <ConfirmQuoteBlock quote={q} selectedQuotes={selectedQuotes} />
          </div>
        ))}
      </div>
    </SidebarLayout>
  );
}

export default QuoteRequestsCart;
